import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  TablePagination,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import Alert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import StatusChip from '../../components/ui/StatusChip';
import SearchBar from '../../components/ui/SearchBar';
import { exportToCsv } from '../../utils/exportCsv';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  search: { maxWidth: 300, flexGrow: 1 },
  count: { color: theme.palette.text.secondary, fontSize: '0.85rem' },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
  },
  passengerCell: { fontWeight: 600 },
  sub: { fontSize: '0.75rem', color: theme.palette.text.secondary },
}));

const STATUS_NORM = {
  Confirmed: 'active',
  confirmed: 'active',
  Pending: 'pending',
  pending: 'pending',
  Cancelled: 'cancelled',
  cancelled: 'cancelled',
  Completed: 'completed',
  completed: 'completed',
};

const STATUS_LABEL = {
  active: 'Confirmée',
  pending: 'En attente',
  cancelled: 'Annulée',
  completed: 'Terminée',
};

const PAY_NORM = {
  Paid: 'paid',
  paid: 'paid',
  Pending: 'pending',
  pending: 'pending',
  Refunded: 'inactive',
  refunded: 'inactive',
  Failed: 'failed',
  failed: 'failed',
};

const PAY_LABEL = {
  paid: 'Payé',
  pending: 'En attente',
  inactive: 'Remboursé',
  failed: 'Échoué',
};

const formatDate = (val) => {
  if (!val) return '—';
  const d = val.toDate ? val.toDate() : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatPrice = (p) => {
  if (p == null) return '—';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(p) + ' FCFA';
};

const BookingsListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(firestore, 'bookings'));
      setBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la récupération des réservations', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleDeleteConfirm = async () => {
    if (!bookingToDelete) return;
    try {
      await deleteDoc(doc(firestore, 'bookings', bookingToDelete.id));
      setBookings((prev) => prev.filter((b) => b.id !== bookingToDelete.id));
      setSnackbar({ open: true, message: 'Réservation supprimée', severity: 'success' });
    } catch (err) {
      console.error('Error deleting booking:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setBookingToDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return bookings.filter((b) => {
      const matchSearch =
        (b.passengerName || b.userName || '').toLowerCase().includes(q) ||
        (b.passengerEmail || b.userEmail || '').toLowerCase().includes(q) ||
        (b.tripName || b.routeName || '').toLowerCase().includes(q) ||
        (b.id || '').toLowerCase().includes(q);
      const norm = STATUS_NORM[b.status] || 'pending';
      const matchStatus = statusFilter === 'all' || norm === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [bookings, searchTerm, statusFilter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExport = () => {
    exportToCsv(
      filtered,
      ['id', 'passengerName', 'passengerEmail', 'tripName', 'departureDate', 'seats', 'totalPrice', 'status', 'paymentStatus'],
      {
        id: 'ID',
        passengerName: 'Passager',
        passengerEmail: 'Email',
        tripName: 'Voyage',
        departureDate: 'Date départ',
        seats: 'Sièges',
        totalPrice: 'Prix total',
        status: 'Statut',
        paymentStatus: 'Paiement',
      },
      'reservations'
    );
  };

  return (
    <Box className="fade-in-up">
      <PageHeader
        breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Réservations' }]}
        title="Réservations"
        subtitle={`${filtered.length} réservation${filtered.length > 1 ? 's' : ''}`}
        actions={
          <>
            <Tooltip title="Exporter CSV">
              <IconButton
                onClick={handleExport}
                style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rafraîchir">
              <IconButton
                onClick={fetchBookings}
                style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/bookings/create')}
            >
              Nouvelle réservation
            </Button>
          </>
        }
      />

      <Paper>
        <Box className={classes.toolbar}>
          <Box className={classes.search}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0); }}
              placeholder="Rechercher par passager, voyage, ID…"
            />
          </Box>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous les statuts</MenuItem>
              <MenuItem value="active">Confirmée</MenuItem>
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="completed">Terminée</MenuItem>
              <MenuItem value="cancelled">Annulée</MenuItem>
            </Select>
          </FormControl>
          <Typography className={classes.count}>
            {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
          </Typography>
        </Box>

        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<ConfirmationNumberIcon style={{ fontSize: 36 }} />}
            title="Aucune réservation"
            description={
              searchTerm || statusFilter !== 'all'
                ? 'Aucune réservation ne correspond à vos filtres.'
                : 'Il n\'y a pas encore de réservations.'
            }
          />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Passager</TableCell>
                    <TableCell>Voyage</TableCell>
                    <TableCell>Date départ</TableCell>
                    <TableCell>Sièges</TableCell>
                    <TableCell>Prix</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Paiement</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((b) => {
                    const norm = STATUS_NORM[b.status] || 'pending';
                    const payNorm = PAY_NORM[b.paymentStatus] || 'pending';
                    return (
                      <TableRow key={b.id} hover>
                        <TableCell>
                          <Box className={classes.passengerCell}>
                            {b.passengerName || b.userName || '—'}
                          </Box>
                          <Box className={classes.sub}>
                            {b.passengerEmail || b.userEmail || ''}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {b.tripName || b.routeName || '—'}
                          </Box>
                        </TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(b.departureDate)}
                        </TableCell>
                        <TableCell>{b.seats || b.numberOfSeats || '—'}</TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {formatPrice(b.totalPrice || b.amount)}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={norm} label={STATUS_LABEL[norm]} />
                        </TableCell>
                        <TableCell>
                          <StatusChip status={payNorm} label={PAY_LABEL[payNorm]} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Voir">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/bookings/${b.id}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(norm === 'active' || norm === 'pending') && (
                            <Tooltip title="Modifier">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/admin/bookings/${b.id}/edit`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              style={{ color: '#ef4444' }}
                              onClick={() => { setBookingToDelete(b); setOpenDeleteDialog(true); }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Par page"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            />
          </>
        )}
      </Paper>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer la réservation de{' '}
            <strong>{bookingToDelete?.passengerName || bookingToDelete?.userName}</strong> ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            style={{ background: '#ef4444', color: '#fff' }}
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingsListPage;
